import { forwardRef } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import type { BaseTextareaProps } from './types';
import { useBaseFieldControl } from './internal/baseFieldContext';
import { resolveBaseAppearance } from './theme';
import { cn } from './utils';

const controlSizeClassNames = {
  sm: 'min-h-20 px-2.5 py-1.5 text-xs leading-5',
  md: 'min-h-24 px-3 py-2 text-sm leading-6',
  lg: 'min-h-28 px-3.5 py-2.5 text-base leading-7',
} as const;

export const BaseTextarea = forwardRef<HTMLTextAreaElement, BaseTextareaProps>(
  function BaseTextarea(
    {
      invalid = false,
      disabled = false,
      readOnly = false,
      controlSize,
      appearance,
      className,
      classNames,
      style,
      'aria-invalid': nativeAriaInvalid,
      onBlur,
      onChange,
      ...textareaProps
    },
    ref,
  ) {
    for (const [name, value] of [
      ['invalid', invalid],
      ['disabled', disabled],
      ['readOnly', readOnly],
    ] as const) {
      if (typeof value !== 'boolean') {
        throw new TypeError(`BaseTextarea ${name} must be a boolean.`);
      }
    }
    if (
      controlSize !== undefined &&
      controlSize !== 'sm' &&
      controlSize !== 'md' &&
      controlSize !== 'lg'
    ) {
      throw new TypeError(
        'BaseTextarea controlSize must be "sm", "md", or "lg".',
      );
    }

    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const fieldControl = useBaseFieldControl();
    const resolvedInvalid = invalid || fieldControl.invalid;

    return (
      <textarea
        {...componentAttributes}
        {...textareaProps}
        ref={ref}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={nativeAriaInvalid ?? (resolvedInvalid || undefined)}
        data-monkeys-component="textarea"
        data-slot="control"
        data-size={controlSize}
        data-invalid={resolvedInvalid || undefined}
        data-disabled={disabled || undefined}
        data-readonly={readOnly || undefined}
        className={cn(
          theme.slots.controlWrapper,
          theme.slots.controlBase,
          theme.slots.controlTextarea,
          resolvedInvalid && theme.slots.controlInvalid,
          disabled && theme.slots.controlDisabled,
          readOnly && theme.slots.controlReadOnly,
          controlSize && controlSizeClassNames[controlSize],
          classNames?.control,
          className,
        )}
        style={{
          ...theme.styles.control,
          ...(resolvedInvalid ? theme.styles.controlInvalid : undefined),
          ...(disabled ? theme.styles.controlDisabled : undefined),
          ...style,
        }}
        onBlur={(event) => {
          onBlur?.(event);
          fieldControl.notifyBlur(event.currentTarget.value);
        }}
        onChange={(event) => {
          onChange?.(event);
          fieldControl.notifyChange(event.currentTarget.value);
        }}
      />
    );
  },
);
